import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { confirmProjectRecovery } from '../../services/api'

const identifier = /^[A-Za-z0-9_-]{1,128}$/

export default function ProjectRecoveryConfirm() {
  const [params] = useSearchParams()
  const [projectId, setProjectId] = useState(params.get('project_id') || '')
  const [operationId, setOperationId] = useState(params.get('operation_id') || '')
  const [confirmed, setConfirmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [restored, setRestored] = useState(false)
  const [message, setMessage] = useState('')
  const [following, setFollowing] = useState(false)
  const preparationSince = useRef<number | undefined>(undefined)
  const pagePaused = useRef(false)
  const inFlight = useRef(false)
  const alive = useRef(true)
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])
  const valid = identifier.test(projectId.trim()) && identifier.test(operationId.trim())
  const inputClass = 'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900'

  const submit = async () => {
    if (!valid || !confirmed || inFlight.current || restored) return
    inFlight.current = true
    setBusy(true)
    setMessage('')
    try {
      const project = projectId.trim(), operation = operationId.trim()
      const storageKey = `project-recovery:${project}:${operation}`
      let requestKey = sessionStorage.getItem(storageKey)
      if (!requestKey) {
        requestKey = crypto.randomUUID()
        sessionStorage.setItem(storageKey, requestKey)
      }
      // Persist before dispatch; an interrupted submission keeps its identity.
      if (!/^[A-Za-z0-9_-]{16,128}$/.test(requestKey)) throw new Error('The saved recovery request is invalid. Ask the administrator to reconcile this operation.')
      setSubmitted(true)
      const retainedCursor = sessionStorage.getItem(storageKey + ':cursor') || undefined
      if (retainedCursor && !/^[a-f0-9]{64}$/.test(retainedCursor)) throw new Error('The saved continuation is invalid. Ask the administrator to reconcile this operation.')
      const result = await confirmProjectRecovery(project, operation, requestKey, retainedCursor)
      if (!alive.current) return
      if (result.cursor) sessionStorage.setItem(storageKey + ':cursor', result.cursor)
      if (result.status === 'EXACT_PARENT_RECONSTRUCTED' || result.status === 'PROJECT_DEPENDENCIES_RECONSTRUCTED') {
        setFollowing(false)
        setRestored(true)
        setMessage(result.status === 'PROJECT_DEPENDENCIES_RECONSTRUCTED'
          ? 'The approved project and dependent records have been restored. Open the project and check its contents and file access. Ask the administrator to verify the operation in Monitor.'
          : 'The project record has been restored. Open the project and check its contents. Ask the administrator to verify the operation in Monitor.')
      } else if (['OWNER_CONFIRMATION_REQUIRED', 'PREPARING', 'PREPARED', 'BATCH_APPLIED'].includes(result.status)) {
        if (result.status === 'PREPARING') preparationSince.current ??= Date.now()
        else preparationSince.current = undefined
        const stalled = preparationSince.current !== undefined && Date.now() - preparationSince.current >= 120000
        setFollowing(!stalled && !pagePaused.current)
        setMessage(stalled
          ? 'Preparation has no confirmed outcome. Automatic continuation has paused. Keep the operation ID and ask the administrator to reconcile it; do not start a new recovery.'
          : result.status === 'PREPARED'
            ? `Your encrypted content is prepared${result.rows_prepared === undefined ? '' : ` for ${result.rows_prepared} records`}. The recovery service is applying the approved batch. This page will continue the same approved request.`
            : result.status === 'OWNER_CONFIRMATION_REQUIRED'
              ? 'The next approved batch is ready. Continuing with your confirmation and the recorded request.'
              : 'Recovery is progressing. Keep this page open while the approved batches complete.')
      } else {
        setFollowing(false)
        setMessage('Recovery needs reconciliation. The existing request has been retained. Check its result again or give the recovery operation ID to the administrator.')
      }
    } catch (failure) {
      if (!alive.current) return
      setFollowing(false)
      const error = failure as { response?: { status?: number }; message?: string }
      if (error.response?.status === 404) {
        setSubmitted(false)
        setConfirmed(false)
      }
      setMessage(error.response?.status === 404
        ? 'This recovery is not available to your signed-in account. Check the project and operation IDs with the administrator.'
        : 'Recovery has not been confirmed. Keep this page and check the same request, or ask the administrator to reconcile the operation. ' + (error.response ? '' : error.message || ''))
    } finally {
      inFlight.current = false
      if (alive.current) setBusy(false)
    }
  }
  const submitRef = useRef(submit)
  submitRef.current = submit
  useEffect(() => {
    if (!following || restored) return
    const timer = window.setInterval(() => { if (!document.hidden) void submitRef.current() }, 5000)
    return () => window.clearInterval(timer)
  }, [following, restored])

  return <main className="mx-auto max-w-3xl space-y-6 p-6" aria-labelledby="project-recovery-title">
    <div><h1 id="project-recovery-title" className="text-2xl font-semibold text-slate-900">Confirm project recovery</h1>
      <p className="mt-2 text-slate-600">Use the project and recovery operation IDs supplied by your administrator. Sign in as the current project owner to confirm the approved recovery.</p></div>
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <fieldset disabled={busy || submitted} className="space-y-4 disabled:opacity-70">
        <label className="block text-sm font-medium">Project ID<input aria-label="Project ID" className={inputClass} value={projectId} onChange={event => setProjectId(event.target.value)} /></label>
        <label className="block text-sm font-medium">Recovery operation ID<input aria-label="Recovery operation ID" className={inputClass} value={operationId} onChange={event => setOperationId(event.target.value)} /></label>
        <p className="text-sm text-slate-600">This action confirms the recovery scope approved by your administrator: the project record, or the project with its supported missing dependent records. Existing records and current access permissions are preserved. Available file versions are checked before the project is published.</p>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={confirmed} onChange={event => setConfirmed(event.target.checked)} />I have checked these IDs and want to restore the approved project scope, including any dependent-record batches in that plan.</label>
      </fieldset>
      {!restored && <button type="button" disabled={!valid || !confirmed || busy} onClick={() => { pagePaused.current = false; void submit() }} className="mt-5 rounded-lg bg-sky-700 px-4 py-2 font-semibold text-white disabled:opacity-50">{busy ? 'Checking recovery…' : submitted ? 'Check recovery result' : 'Confirm project recovery'}</button>}
      {following && <button type="button" onClick={() => { pagePaused.current = true; setFollowing(false) }} className="ml-3 mt-5 text-sm font-medium text-sky-700">Pause page updates</button>}
      {message && <p role="status" aria-live="polite" className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-800">{message}</p>}
      {restored && <Link className="mt-4 inline-block font-semibold text-sky-700" to={`/project/${encodeURIComponent(projectId.trim())}`}>Open restored project</Link>}
    </div>
    <Link className="text-sm font-medium text-sky-700" to="/projects">Back to projects</Link>
  </main>
}
